import React, { useCallback, useContext, useEffect, useState } from "react";
import { useInterceptor } from "./InterceptorProvider";
import { clearSessionData } from "services/session.service";

const defaultUser = {};

const UserContext = React.createContext({
  user: defaultUser,
  hasTreeData: false,
  updateUser: () => {},
  login: async () => {},
  logout: async () => {},
  updatePersonalInfo: async () => {},
  updatePassword: async () => {},
});

export const UserProvider = ({ children }) => {
  const { api } = useInterceptor();
  const [user, setUser] = useState(defaultUser);
  const [hasTreeData, setHasTreeData] = useState(false);

  const updateUser = useCallback((inputUser) => {
    setUser((prevState) => ({ ...prevState, ...inputUser }));
  }, []);

  const getUser = useCallback(async () => {
    try {
      const response = await api.get("/auth/user");
      if (response?.data?.admin) {
        updateUser(response.data.admin);
        return response;
      }
    } catch (err) {
      console.log(err);
      return err.response.data.message;
    }
  }, [api, updateUser]);

  const login = useCallback(
    async (data) => {
      try {
        const response = await api.post("/auth/login", data);
        getUser();
        return response;
      } catch (err) {
        return Promise.reject(err);
      }
    },
    [api, getUser]
  );

  const updatePersonalInfo = useCallback(async (data, id) => {
    try {
      const response = await api.put(
        `/account/update/personal-info/${id}`,
        data
      );
      return response;
    } catch (err) {
      return Promise.reject(err);
    }
  });

  const updatePassword = useCallback(async (data, id) => {
    try {
      console.log("from provider", id);
      const response = await api.put(`/account/update/password/${id}`, data);
      return response;
    } catch (err) {
      return Promise.reject(err);
    }
  });

  const logout = useCallback(async () => {
    try {
      const response = await api.post("/auth/logout", {});
      setUser({});
      clearSessionData();
      return response;
    } catch (err) {
      return err.response.data.message;
    }
  }, [api]);

  const fetchTreeData = useCallback(async () => {
    if (!user.organizationId) {
      return;
    }

    try {
      const response = await api.get(`/getOrganizationTree`, {
        params: { organizationId: user.organizationId },
      });

      if (response.data.success) {
        setHasTreeData(true);
      }
    } catch (error) {
      console.error(
        "Error fetching tree data:",
        error.response?.data?.message || error?.message
      );
    }
  }, [api, user.organizationId]);

  useEffect(() => {
    if (user.organizationId) {
      fetchTreeData();
    }
  }, [user, fetchTreeData]);

  useEffect(() => {
    if (user._id) {
      return;
    }
    async function fetchUser() {
      const response = await getUser();
      if (response === "jwt expired") {
        clearSessionData();
      }
      // console.log("user from user effect -> ", response);
    }
    fetchUser();
  }, [user, getUser, updateUser]);

  return (
    <UserContext.Provider
      value={{
        user,
        updateUser,
        login,
        updatePersonalInfo,
        updatePassword,
        logout,
        hasTreeData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export function useUser() {
  const userContext = useContext(UserContext);

  return userContext;
}
